import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { Observable, of } from 'rxjs';
import { tap } from 'rxjs/operators';

import { AuthorizationService, MagnetService } from '@mirage-projects/cw-commons';
import { TokenResponseData } from '@mirage-projects/cw-models';

import { CWEnvironmentConfiguration } from '../services/environment-configuration';
import { EnvironmentConfigurationService } from '../services/environment-configuration.service';

@Injectable({ providedIn: 'root' })
export class AuthTokenInterceptor implements HttpInterceptor {
    private environmentConfig: CWEnvironmentConfiguration = this.envConfigService.getAppConfig();

    constructor(
        private authorizationService: AuthorizationService,
        private magnetService: MagnetService,
        private envConfigService: EnvironmentConfigurationService
    ) {}

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        // If requests get-token
        if (request.body && request.url === this.environmentConfig.topconOne.auth_backend) {
            // Update request data
            request = request.clone({
                method: 'POST',
                setParams: {
                    code: request.body.code,
                    redirect: location.origin,
                    channel: 'web',
                    app: this.magnetService.getAppName(),
                },
            });
        } else {
            if (request.url.startsWith(this.environmentConfig.baseURL) || request.url.startsWith(this.environmentConfig.publicApiUrl)) {
                let headers = request.headers.append(
                    'authorization',
                    `${this.authorizationService.getAuthType()} ${this.authorizationService.getToken()}`
                );
                headers = headers.append('accept', 'application/json; version=2');
                if (request.url.startsWith(this.environmentConfig.publicApiUrl)) {
                    headers = headers.append('x-api-key', this.environmentConfig.publicApiKey);
                }
                request = request.clone({
                    headers,
                });
            }
            if (this.magnetService.isSite()) {
                if (request.url.indexOf('/ping') >= 0) {
                    // Simulate response for site
                    const mockResponseForSite = { success: true, timestamp: new Date() };
                    const httpResponse = new HttpResponse({ status: 200, body: mockResponseForSite });
                    return of(httpResponse);
                }
                if (request.url.indexOf('/languages') >= 0) {
                    // Simulate response for site
                    const mockResponseForSite = [
                        { name: 'English', localized: 'English', code: 'en_US' },
                        { name: 'Japanese', localized: '日本語', code: 'ja' },
                        { name: 'Spanish', localized: 'Español', code: 'es' },
                    ];
                    const httpResponse = new HttpResponse({ status: 200, body: mockResponseForSite });
                    return of(httpResponse);
                }
            }
        }

        // Handle responses
        return next.handle(request).pipe(
            tap((event) => {
                if (this.magnetService.isSite()) {
                    return;
                }

                const isHttpResponse = event instanceof HttpResponse;
                const hasUrlBackAuth = (event as HttpResponse<TokenResponseData>).url?.startsWith(
                    this.envConfigService.getAppConfig().topconOne.auth_backend
                );
                if (!isHttpResponse || !hasUrlBackAuth) {
                    return;
                }
                const body = event.body as TokenResponseData;
                this.authorizationService.setCapabilities(body.capabilities);
                const tokenUser = body?.user;
                const tokenResponse = body?.api_access_token;

                if (tokenResponse) {
                    this.authorizationService.setToken(tokenResponse);
                }

                if (tokenUser) {
                    tokenUser.isSupport = !!body.company?.support;
                    const user = this.authorizationService.tokenUserToUser(tokenUser);
                    this.authorizationService.setCurrentUser(user);
                }
            })
        );
    }
}
